import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { PaginationResponse } from '../types/Dtos/response';
import { OrderDirection } from '../utils/constants';
import { useLocalStorage } from './useLocalStorage';

// Generic type for the data model
type DataModel = Record<string, any>;

// Type for filter keys
type FilterKeys<T extends DataModel> = keyof T | string;

// Options for the hook
interface UseFilterSortPaginateOptions<T extends DataModel> {
  queryKey: (string | boolean) | (string | boolean)[];
  fetchData: (params: Record<string, any>) => Promise<PaginationResponse<T>>;
  initialSort?: { key: keyof T; direction: OrderDirection };
  initialPageSize?: number;
  initialFilters?: Record<string, string[]>;
  storageKey?: string;
  enabled?: boolean;
}

// Hook return type
interface UseFilterSortPaginateResult<T extends DataModel> {
  data: T[];
  total: number;
  isLoading: boolean;
  isRefetching: boolean;
  error: unknown;
  page: number;
  pageSize: number;
  pageCount: number;
  orderBy: keyof T | null;
  orderDirection: OrderDirection;
  filters: Record<string, string[]>;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setOrderBy: (key: keyof T, direction: OrderDirection) => void;
  setFilter: (key: FilterKeys<T>, value: string[]) => void;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  handleSort: (key: string) => void;
  getColumnOrder: (key: string) => OrderDirection | undefined;
  handleFiltersChange: (filters: Record<string, string[]>) => void;
  handleFiltersClear: (filters?: Record<string, string[]>) => void;
}

export function useFilterSortPaginate<T extends DataModel>({
  queryKey,
  fetchData,
  initialSort,
  initialFilters,
  storageKey,
  initialPageSize = 20,
  enabled,
}: UseFilterSortPaginateOptions<T>): UseFilterSortPaginateResult<T> {
  const [localPage, setLocalPage] = useState(() => 1);
  const [localPageSize, setLocalPageSize] = useState(() => initialPageSize);
  const [localFilters, setLocalFilters] = useState<Record<string, string[]>>(
    () => initialFilters || {},
  );

  const [storedPage, setStoredPage, removeStoredPage] = useLocalStorage(
    storageKey ? `${storageKey}-page` : '__no_storage_page__',
    1,
  );
  const [storedPageSize, setStoredPageSize, removeStoredPageSize] = useLocalStorage(
    storageKey ? `${storageKey}-pageSize` : '__no_storage_pageSize__',
    initialPageSize,
  );
  const [storedFilters, setStoredFilters, removeStoredFilters] = useLocalStorage<
    Record<string, string[]>
  >(storageKey ? `${storageKey}-filters` : '__no_storage_filters__', initialFilters || {});

  const [page, setPage] = storageKey ? [storedPage, setStoredPage] : [localPage, setLocalPage];
  const [pageSize, setPageSize] = storageKey
    ? [storedPageSize, setStoredPageSize]
    : [localPageSize, setLocalPageSize];
  const [filters, setFilters] = storageKey
    ? [storedFilters, setStoredFilters]
    : [localFilters, setLocalFilters];

  const [orderBy, setOrderBy] = useState<keyof T | null>(() => initialSort?.key || null);
  const [orderDirection, setOrderDirection] = useState<OrderDirection>(
    () => initialSort?.direction || ('asc' as OrderDirection),
  );

  const tableMetaKeys = [page, pageSize, orderBy, orderDirection, filters];
  const preparedQueryKey = Array.isArray(queryKey)
    ? [...queryKey, ...tableMetaKeys]
    : [queryKey, ...tableMetaKeys];
  // Fetch data using React Query
  const { data, isLoading, isRefetching, error } = useQuery({
    queryKey: preparedQueryKey,
    queryFn: async () =>
      fetchData({
        page,
        pageSize,
        orderBy,
        orderDirection,
        ...filters,
      }),
    placeholderData: keepPreviousData,
    enabled,
  });

  const setPageWrapper = useCallback((newPage: number) => {
    setPage(newPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPageSizeWrapper = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSortWrapper = useCallback((key: keyof T, direction: OrderDirection) => {
    setOrderBy(key);
    setOrderDirection(direction);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilterWrapper = useCallback(
    (key: FilterKeys<T>, value: string[]) => {
      setFilters((prev) => {
        const newFilters = { ...prev };

        if (!value || value.length === 0) {
          delete newFilters[key as string];
        } else {
          newFilters[key as string] = value;
        }

        return newFilters;
      });
      setPage(1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters],
  );

  const handleSort = useCallback(
    (key: string) => {
      if (orderBy === key) {
        setSortWrapper(key, orderDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortWrapper(key, 'asc');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderDirection, orderBy],
  );

  /** Apparently, API doesn't always expect the same column keys it's sending, so this argument
   * has to be a generic string or hard typed for all lists */
  const getColumnOrder = useCallback(
    (key: string) => (orderBy === key ? orderDirection : undefined),
    [orderBy, orderDirection],
  );

  const handleFiltersChange = useCallback((filters: Record<string, string[]>) => {
    Object.entries(filters).forEach(([groupName, selectedOptions]) => {
      setFilterWrapper(groupName, selectedOptions);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFiltersWrapper = useCallback(
    (newFilters: React.SetStateAction<Record<string, string[]>>) => {
      setFilters(newFilters);
      setPage(1);
    },
    [setFilters, setPage],
  );

  const handleFiltersClear = useCallback(
    (filters?: Record<string, string[]>) => {
      setFilters(filters ? filters : {});
      setPage(1);
    },
    [setFilters, setPage],
  );

  return {
    data: data?.data || [],
    total: data?.totalCount || 0,
    pageCount: data?.pageCount || 0,
    isLoading,
    isRefetching,
    error,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filters,
    setPage: setPageWrapper,
    setPageSize: setPageSizeWrapper,
    setOrderBy: setSortWrapper,
    setFilter: setFilterWrapper,
    setFilters: setFiltersWrapper,
    handleSort,
    getColumnOrder,
    handleFiltersChange,
    handleFiltersClear,
  };
}
